import { useMemo, useCallback } from 'react';
import { useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { useAppContext } from 'QuoteApp/services/providers';
import {
  getADPFCommand,
  getIsEnabled,
  getIsDisabled,
  getIsLoading,
  getTooltipMessage,
} from './utils';

export const useADPFReport = () => {
  const { toast } = useToast();
  const { appId, details, setDetails } = useAppContext();

  const [{ loading: ADPFIsLoading }, triggerReport] = useLazyQuoteApi({
    method: 'POST',
    url: `/insurance_applications/${appId}/reports/auto_data_prefill`,
  });

  const ADPFCommand = useMemo(() => getADPFCommand(details.commands.reports), [
    details.commands.reports,
  ]);

  const isEnabled = useMemo(() => getIsEnabled(ADPFCommand), [ADPFCommand]);

  const isLoading = useMemo(
    () => getIsLoading(ADPFCommand?.status, ADPFIsLoading),
    [ADPFCommand?.status, ADPFIsLoading]
  );

  const isDisabled = useMemo(() => getIsDisabled(ADPFCommand?.status), [
    ADPFCommand?.status,
  ]);

  const tooltipMessage = useMemo(() => getTooltipMessage(ADPFCommand?.status), [
    ADPFCommand?.status,
  ]);

  const runReport = useCallback(async () => {
    if (!ADPFCommand?.runnable || ADPFCommand?.status === 'is_running') return;

    try {
      await triggerReport();
      setDetails(oldDetails => ({
        ...oldDetails,
        status: 'third_party_in_progress',
      }));
    } catch (error) {
      toast({
        type: 'error',
        message: 'Failed to start ADPF report. Please try again.',
        withSecondaryNav: false,
        icon: 'error',
      });
    }
  }, [
    ADPFCommand?.runnable,
    ADPFCommand?.status,
    triggerReport,
    setDetails,
    toast,
  ]);

  return {
    isEnabled,
    isLoading,
    isDisabled,
    tooltipMessage,
    runReport,
  };
};
